import React from 'react';
import { Coffee, CheckCircle, Star, ArrowRight, Mail, Phone } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const CustomBranding: React.FC = () => {
  const examples = [
    {
      name: 'Sagan Harborside Blend',
      industry: 'Real Estate',
      description: 'Perfect for closing gifts that clients will actually use and remember'
    },
    {
      name: 'Smith & Sons Morning Roast',
      industry: 'Home Services',
      description: 'A warm thank-you for trusting your team with their home'
    },
    {
      name: 'Marblehead Bank Brew',
      industry: 'Financial Services',
      description: 'Community-focused branding that aligns with local presence'
    }
  ];

  const benefits = [
    {
      icon: Coffee,
      title: 'Actually Gets Used',
      description: 'Coffee gets brewed, shared, and talked about every morning — unlike those branded pens collecting dust in junk drawers.'
    },
    {
      icon: Star,
      title: 'Local & Fresh',
      description: 'Roasted in Marblehead, MA with obsessive attention to quality and craft. Your clients taste the difference.'
    },
    {
      icon: CheckCircle,
      title: 'Custom Storytelling',
      description: 'Every bag includes a QR code linking to your personalized landing page with your story and message.'
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Choose Your Roast',
      description: 'Pick from our lineup of premium roasts — bold, smooth, or seasonal blends. We\'ll help you find the perfect match for your brand personality.'
    },
    {
      number: '02',
      title: 'Add Your Brand',
      description: 'We design custom labels featuring your logo, brand colors, and message. Each bag includes a scannable QR code linking to your personalized story.'
    },
    {
      number: '03',
      title: 'Order Ahead',
      description: 'Purchase bags upfront for the year. Give us 2 weeks\' notice before you need them, and we\'ll roast and ship them fresh.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#132947]">
      <Header />
      
      <div className="container mx-auto px-4 py-20">
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto text-center mb-20">
          <div className="text-6xl mb-8">☕</div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Custom-Branded Coffee
            <span className="block text-[#fda36b]">For Your Business</span>
          </h1>
          <p className="text-xl text-white/80 mb-8 max-w-3xl mx-auto leading-relaxed">
            Stop wasting money on pens and calendars that end up in junk drawers. Give your clients something they'll 
            actually use, enjoy, and remember. With Bond Coffee Custom Branding, we create <strong>freshly roasted, 
            locally crafted coffee</strong> that carries <strong>your brand's story</strong> on every bag.
          </p>
          
          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <a
              href="/custom-branding/example"
              className="inline-flex items-center gap-2 bg-white/10 text-white px-6 py-3 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 border border-white/20 hover:border-[#fda36b]/30"
            >
              See What Your Custom Coffee Could Look Like
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
          
          <div className="bg-gradient-to-r from-[#fda36b]/20 via-[#fda36b]/10 to-[#fda36b]/20 backdrop-blur-sm p-6 rounded-xl border border-[#fda36b]/30">
            <p className="text-white/90 text-lg">
              <span className="font-semibold text-[#fda36b]">Perfect for:</span> Realtors, Home Service Providers, 
              Financial Advisors, Law Firms, Banks, and any business that values lasting client relationships.
            </p>
          </div>
        </div>

        {/* How It Works */}
        <section className="mb-20">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-16 text-[#fda36b]">
              How It Works
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {steps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-gradient-to-br from-white/10 to-white/5 p-8 rounded-2xl border border-white/10 hover:border-[#fda36b]/30 transition-all duration-500 h-full">
                    <div className="text-6xl font-bold text-[#fda36b]/20 mb-4">{step.number}</div>
                    <h3 className="text-2xl font-bold text-white mb-4">{step.title}</h3>
                    <p className="text-white/80 leading-relaxed">{step.description}</p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                      <ArrowRight className="w-8 h-8 text-[#fda36b]/40" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Coffee Works */}
        <section className="mb-20">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-16 text-[#fda36b]">
              Why Coffee Works
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-gradient-to-br from-white/10 to-white/5 p-8 rounded-2xl border border-white/10 hover:border-[#fda36b]/30 transition-all duration-500">
                  <benefit.icon className="w-16 h-16 text-[#fda36b] mb-6" />
                  <h3 className="text-2xl font-bold text-white mb-4">{benefit.title}</h3>
                  <p className="text-white/80 leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Examples */}
        {/* Features Highlight */}
        <section className="mb-20">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-white/10 to-white/5 p-8 rounded-2xl border border-white/10">
              <h2 className="text-3xl font-bold text-center mb-8 text-[#fda36b]">
                What's Included
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#fda36b] mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="text-white font-semibold">Custom Label Design</h4>
                      <p className="text-white/80 text-sm">Your logo, colors, and messaging professionally integrated</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#fda36b] mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="text-white font-semibold">QR Code Integration</h4>
                      <p className="text-white/80 text-sm">Links to your personalized landing page with your story</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#fda36b] mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="text-white font-semibold">Fresh Roasting</h4>
                      <p className="text-white/80 text-sm">Roasted to order within 2 weeks of your request</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#fda36b] mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="text-white font-semibold">Flexible Ordering</h4>
                      <p className="text-white/80 text-sm">From 50 bags for a season to 500+ for the year</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#fda36b] mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="text-white font-semibold">Premium Quality</h4>
                      <p className="text-white/80 text-sm">Same obsessive attention to quality as our retail blends</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#fda36b] mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="text-white font-semibold">Local Craft Story</h4>
                      <p className="text-white/80 text-sm">Marblehead-roasted with a story your clients will love</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="mb-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-gradient-to-br from-[#fda36b]/20 to-[#fda36b]/10 p-12 rounded-2xl border border-[#fda36b]/30">
              <h2 className="text-4xl font-bold text-white mb-6">
                Ready to Get Started?
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Whether you want <strong>50 bags for a season</strong> or <strong>500 for the year</strong>, 
                we'll make the process simple and stress-free. No more wondering if your promotional budget 
                is actually working.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <a
                  href="mailto:hello@thebondcoffee.com?subject=Custom Branding Inquiry"
                  className="inline-flex items-center gap-3 bg-gradient-to-r from-[#fda36b] to-[#fda36b]/80 text-[#132947] px-8 py-4 rounded-lg font-bold text-xl hover:from-[#fda36b]/90 hover:to-[#fda36b]/70 transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  <Mail className="w-6 h-6" />
                  Email us at hello@thebondcoffee.com
                </a>
                
                <div className="text-white/80">
                  <p className="text-sm">Questions?</p>
                  <a 
                    href="mailto:hello@thebondcoffee.com" 
                    className="text-[#fda36b] hover:text-[#fda36b]/80 underline font-semibold"
                  >
                    hello@thebondcoffee.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process Details */}
        <section>
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/5 p-8 rounded-2xl border border-white/10">
              <h3 className="text-2xl font-bold text-white mb-6 text-center">
                The Process is Simple
              </h3>
              
              <div className="space-y-6 text-white/80">
                <div className="flex items-start gap-4">
                  <span className="text-[#fda36b] font-bold text-lg">1.</span>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Initial Consultation</h4>
                    <p>We'll discuss your brand, target audience, and goals to find the perfect roast match.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <span className="text-[#fda36b] font-bold text-lg">2.</span>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Design & Approval</h4>
                    <p>Our team creates custom label designs and QR-linked landing pages for your approval.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <span className="text-[#fda36b] font-bold text-lg">3.</span>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Order & Delivery</h4>
                    <p>Place your order with 2 weeks' notice. We roast fresh and ship directly to you.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <span className="text-[#fda36b] font-bold text-lg">4.</span>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Delight Your Clients</h4>
                    <p>Watch as your clients enjoy something truly memorable that keeps your brand top-of-mind.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      
      <Footer />
    </div>
  );
};

export default CustomBranding;